/**
 * BILLING PROFILE — plan-014 D1a (design §27.3.5, FR12-BIL-01; ADR 0034 decision 2).
 *
 * The legal payer of an account — legal and trading name, country, address, tax identifier, billing
 * recipients, accounting reference, currency — effective-dated, one in force per account (migration 0080's
 * exclusion constraint). A change is a successor row, never an edit: the closed row is the history, and every
 * issued financial document keeps its own immutable snapshot (D5b). Tax evidence and the payer kind are D5a's
 * (0094); a tax identifier's shape per country is PRIM-T's `localTaxIdShapeOf` — here it is only normalised.
 */
import { CURRENCY_CODE } from "./money.js";
import { ValidationError } from "../error/taxonomy.js";
import { idCodec } from "../id/brand.js";
/** Parse / guard / mint for {@link BillingProfileId}. */
export const billingProfileIdCodec = idCodec("BillingProfileId");
/** The bounds migration 0080 enforces, restated so a request is refused before it reaches the database. */
export const BILLING_PROFILE_BOUND = {
    addressLineMax: 6,
    billingEmailMax: 10,
    taxIdLengthMax: 64,
};
const COUNTRY_SHAPE = /^[A-Z]{2}$/;
/** Deliberately loose: one `@`, something either side, a dot in the domain, no whitespace. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function refuse(field, message) {
    throw new ValidationError({ code: "validation_failed", message, field });
}
function required(value, field) {
    const trimmed = value.trim();
    if (trimmed === "")
        refuse(field, `${field} is required`);
    return trimmed;
}
function optional(value) {
    if (value === null)
        return null;
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
}
/** Whitespace removed; `null` when nothing is left. Shape per country is PRIM-T's, not this function's. */
export function normaliseTaxId(value) {
    if (value === null)
        return null;
    const compact = value.replace(/\s+/g, "");
    if (compact === "")
        return null;
    if (compact.length > BILLING_PROFILE_BOUND.taxIdLengthMax) {
        refuse("tax_id", `tax_id is longer than ${String(BILLING_PROFILE_BOUND.taxIdLengthMax)} characters`);
    }
    return compact;
}
/**
 * Validates and normalises a legal payer's details. Throws `validation_failed` naming the field; never fills a
 * missing value in. Duplicate billing recipients (compared case-insensitively, as the `CITEXT` column does)
 * are refused rather than silently merged.
 */
export function parseBillingProfileDetail(input) {
    const legalName = required(input.legalName, "legal_name");
    const city = required(input.city, "city");
    const country = input.country.trim().toUpperCase();
    if (!COUNTRY_SHAPE.test(country))
        refuse("country", "country must be an ISO 3166-1 alpha-2 code, e.g. GR");
    const addressLine = input.addressLine.map((one) => one.trim()).filter((one) => one !== "");
    if (addressLine.length === 0)
        refuse("address_line", "address_line needs at least one line");
    if (addressLine.length > BILLING_PROFILE_BOUND.addressLineMax) {
        refuse("address_line", `address_line takes at most ${String(BILLING_PROFILE_BOUND.addressLineMax)} lines`);
    }
    const billingEmail = input.billingEmail.map((one) => one.trim());
    if (billingEmail.length === 0)
        refuse("billing_email", "billing_email needs at least one recipient");
    if (billingEmail.length > BILLING_PROFILE_BOUND.billingEmailMax) {
        refuse("billing_email", `billing_email takes at most ${String(BILLING_PROFILE_BOUND.billingEmailMax)} recipients`);
    }
    const seen = new Set();
    for (const one of billingEmail) {
        if (!EMAIL_SHAPE.test(one))
            refuse("billing_email", `${JSON.stringify(one)} is not an email address`);
        const folded = one.toLowerCase();
        if (seen.has(folded))
            refuse("billing_email", `${JSON.stringify(one)} is listed twice`);
        seen.add(folded);
    }
    const currency = input.currency.trim().toUpperCase();
    if (currency !== CURRENCY_CODE)
        refuse("currency", `currency must be ${CURRENCY_CODE}`);
    return {
        legalName,
        tradingName: optional(input.tradingName),
        country,
        addressLine,
        postalCode: optional(input.postalCode),
        city,
        taxId: normaliseTaxId(input.taxId),
        billingEmail,
        accountingReference: optional(input.accountingReference),
        currency: CURRENCY_CODE,
    };
}
//# sourceMappingURL=profile.js.map